import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowDownRight } from "lucide-react";
import { MaskedLines } from "@/components/Reveal";
import MonoVideo from "@/components/MonoVideo";

export default function Hero({ heroImage, heroVideo, onApply }) {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start start", "end start"],
  });
  const imgY = useTransform(scrollYProgress, [0, 1], ["0%", "28%"]);
  const imgScale = useTransform(scrollYProgress, [0, 1], [1, 1.15]);
  const textY = useTransform(scrollYProgress, [0, 1], ["0%", "-40%"]);

  return (
    <section
      id="top"
      ref={ref}
      data-testid="hero-section"
      className="relative min-h-screen w-full overflow-hidden bg-[#0a0a0a] flex items-center pt-20"
    >
      {/* Parallax image on the right */}
      <motion.div
        style={{ y: imgY, scale: imgScale }}
        className="absolute right-0 top-0 h-full w-full md:w-[52%] z-0"
      >
        {heroVideo ? (
          <MonoVideo
            src={heroVideo}
            alt="VOGUEVERVE editorial reel"
            testId="hero-video"
            className="h-full w-full"
            videoClassName="h-full w-full object-cover object-center"
          />
        ) : (
          <img
            src={heroImage}
            alt="VOGUEVERVE editorial model"
            className="h-full w-full object-cover object-center"
          />
        )}
        <div className="absolute inset-0 bg-gradient-to-r from-[#0a0a0a] via-[#0a0a0a]/40 to-transparent md:from-[#0a0a0a] md:via-transparent md:to-transparent" />
        <div className="absolute inset-0 bg-black/30 md:bg-transparent" />
      </motion.div>

      {/* Kinetic text */}
      <motion.div
        style={{ y: textY }}
        className="relative z-10 max-w-[1600px] w-full mx-auto px-6 md:px-10"
      >
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.1, duration: 0.6 }}
          className="flex items-center gap-4 mb-8"
        >
          <span className="h-px w-12 bg-[#ffffff]" />
          <span className="text-xs uppercase tracking-[0.3em] font-bold text-[#ffffff]/70">
            Model Management &middot; Est. MMXXV
          </span>
        </motion.div>

        <h1 className="font-display font-black uppercase leading-[0.85] tracking-tighter text-[#ffffff] text-[13vw] md:text-[8.5vw] lg:text-[8rem]">
          <MaskedLines lines={["Dominate"]} startDelay={0.3} />
          <MaskedLines lines={["The Industry"]} startDelay={0.5} lineClassName="text-stroke" />
        </h1>

        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.95, duration: 0.8 }}
          className="font-script text-[#ffffff] text-5xl md:text-7xl leading-none mt-6"
        >
          be the next show-stopper.
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.1, duration: 0.8 }}
          className="mt-10 flex flex-col sm:flex-row sm:items-end gap-8 max-w-2xl"
        >
          <p className="text-base md:text-lg text-[#ffffff]/70 leading-relaxed max-w-md">
            You have the face. We have the map. VOGUEVERVE turns raw potential into
            campaign-ready careers — profiles, brand deals, and the ambassadorships you dreamt of.
          </p>
          <button
            data-testid="hero-apply-btn"
            onClick={onApply}
            className="group inline-flex items-center gap-3 rounded-full bg-[#ffffff] text-[#0a0a0a] hover:bg-[#000000] hover:text-white px-8 py-4 text-xs uppercase tracking-[0.2em] font-bold transition-colors duration-300 shrink-0"
          >
            Get Scouted
            <ArrowDownRight size={18} strokeWidth={2} className="transition-transform duration-300 group-hover:translate-x-1 group-hover:translate-y-1" />
          </button>
        </motion.div>
      </motion.div>

      {/* Bottom scroll hint */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.4, duration: 1 }}
        className="absolute bottom-8 left-6 md:left-10 z-10 flex items-center gap-3 text-[#ffffff]/50"
      >
        <span className="text-[10px] uppercase tracking-[0.3em] font-bold">Scroll</span>
        <motion.span
          animate={{ y: [0, 6, 0] }}
          transition={{ repeat: Infinity, duration: 1.6 }}
          className="h-8 w-px bg-[#ffffff]/40"
        />
      </motion.div>
    </section>
  );
}
